import { isSupabaseConfigured } from "@/lib/env";
import { invokeFunction } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/features/auth/types";

export type DocumentStatus = "new" | "in_review" | "approved" | "rejected";
export type DocumentKind =
  | "bol"
  | "pod"
  | "cdl"
  | "medical_card"
  | "insurance"
  | "w9"
  | "resume"
  | "other";

export interface DocumentRecord {
  id: string;
  ownerId: string | null;
  ownerName: string;
  ownerEmail: string;
  kind: DocumentKind;
  status: DocumentStatus;
  storagePath: string;
  contentType: string;
  sizeBytes: number;
  relatedReference: string;
  internalNotes: string;
  createdAt: string;
}

export interface UploadDocumentInput {
  file: File;
  kind: DocumentKind;
  relatedReference: string;
  owner: AppUser;
}

interface DocumentRow {
  id: string;
  owner_id: string | null;
  kind: string;
  storage_path: string;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
  status?: DocumentStatus | null;
  related_reference?: string | null;
  internal_notes?: string | null;
  profiles?:
    | {
        full_name: string | null;
        email: string | null;
      }
    | Array<{
        full_name: string | null;
        email: string | null;
      }>
    | null;
}

const LS_KEY = "bb_documents";

const seedDocuments: DocumentRecord[] = [
  {
    id: "doc-bol-10482",
    ownerId: "profile-driver",
    ownerName: "Preview Driver",
    ownerEmail: "driver@example.com",
    kind: "bol",
    status: "new",
    storagePath: "preview/documents/BB-10482-bol.pdf",
    contentType: "application/pdf",
    sizeBytes: 342_000,
    relatedReference: "BB-10482",
    internalNotes: "",
    createdAt: "2026-06-17T14:05:00.000Z",
  },
  {
    id: "doc-pod-10485",
    ownerId: "profile-driver",
    ownerName: "Preview Driver",
    ownerEmail: "driver@example.com",
    kind: "pod",
    status: "in_review",
    storagePath: "preview/documents/BB-10485-pod.pdf",
    contentType: "application/pdf",
    sizeBytes: 418_000,
    relatedReference: "BB-10485",
    internalNotes: "Confirm delivery timestamp with dispatch.",
    createdAt: "2026-06-17T13:20:00.000Z",
  },
  {
    id: "doc-insurance-carrier",
    ownerId: "profile-carrier",
    ownerName: "Preview Owner Operator",
    ownerEmail: "owneroperator@example.com",
    kind: "insurance",
    status: "approved",
    storagePath: "preview/documents/carrier-insurance.pdf",
    contentType: "application/pdf",
    sizeBytes: 256_000,
    relatedReference: "Owner-operator onboarding",
    internalNotes: "",
    createdAt: "2026-06-16T17:30:00.000Z",
  },
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;
const localId = () =>
  `doc-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now()}`;

function rowToDocument(row: DocumentRow): DocumentRecord {
  const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles;
  return {
    id: row.id,
    ownerId: row.owner_id,
    ownerName: profile?.full_name ?? "",
    ownerEmail: profile?.email ?? "",
    kind: normalizeKind(row.kind),
    status: row.status ?? "new",
    storagePath: row.storage_path,
    contentType: row.content_type ?? "",
    sizeBytes: row.size_bytes ?? 0,
    relatedReference: row.related_reference ?? "",
    internalNotes: row.internal_notes ?? "",
    createdAt: row.created_at,
  };
}

function normalizeKind(kind: string): DocumentKind {
  const allowed: DocumentKind[] = [
    "bol",
    "pod",
    "cdl",
    "medical_card",
    "insurance",
    "w9",
    "resume",
    "other",
  ];
  return allowed.includes(kind as DocumentKind) ? (kind as DocumentKind) : "other";
}

function localReadAll(): DocumentRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as DocumentRecord[];
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedDocuments));
  return [...seedDocuments];
}

function localWriteAll(documents: DocumentRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(documents));
}

function safeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, "_").slice(-100) || "document";
}

const byNewest = (a: DocumentRecord, b: DocumentRecord) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

export async function listDocuments(): Promise<DocumentRecord[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("documents")
      .select(
        "id, owner_id, kind, storage_path, content_type, size_bytes, created_at, status, related_reference, internal_notes, profiles(full_name, email)",
      )
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data as DocumentRow[]).map(rowToDocument);
  }
  return localReadAll().sort(byNewest);
}

export async function listMyDocuments(ownerId: string): Promise<DocumentRecord[]> {
  if (!ownerId) return [];
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("documents")
      .select(
        "id, owner_id, kind, storage_path, content_type, size_bytes, created_at, status, related_reference, internal_notes, profiles(full_name, email)",
      )
      .eq("owner_id", ownerId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data as DocumentRow[]).map(rowToDocument);
  }
  return localReadAll()
    .filter((document) => document.ownerId === ownerId)
    .sort(byNewest);
}

export async function updateDocument(
  id: string,
  changes: { status?: DocumentStatus; internalNotes?: string },
): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("documents-admin", {
      action: "update",
      id,
      ...(changes.status ? { status: changes.status } : {}),
      ...(changes.internalNotes !== undefined ? { internalNotes: changes.internalNotes } : {}),
    });
    return;
  }

  localWriteAll(
    localReadAll().map((document) =>
      document.id === id
        ? {
            ...document,
            status: changes.status ?? document.status,
            internalNotes: changes.internalNotes ?? document.internalNotes,
          }
        : document,
    ),
  );
}

export async function uploadDocument({
  file,
  kind,
  relatedReference,
  owner,
}: UploadDocumentInput): Promise<void> {
  const now = new Date().toISOString();

  if (supabaseReady()) {
    const path = `${owner.id}/${crypto.randomUUID()}-${safeFileName(file.name)}`;
    const { error: uploadError } = await supabase!.storage
      .from("documents")
      .upload(path, file, { contentType: file.type || "application/octet-stream" });
    if (uploadError) throw uploadError;

    const { error: insertError } = await supabase!.from("documents").insert({
      owner_id: owner.id,
      kind,
      storage_path: path,
      content_type: file.type || null,
      size_bytes: file.size,
      related_reference: relatedReference.trim(),
      status: "new",
    });
    if (insertError) throw insertError;
    return;
  }

  const record: DocumentRecord = {
    id: localId(),
    ownerId: owner.id,
    ownerName: owner.name,
    ownerEmail: owner.email,
    kind,
    status: "new",
    storagePath: `preview/documents/${safeFileName(file.name)}`,
    contentType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    relatedReference: relatedReference.trim(),
    internalNotes: "",
    createdAt: now,
  };

  localWriteAll([record, ...localReadAll()]);
}

export async function getDocumentUrl(path: string): Promise<string | null> {
  if (!supabaseReady()) return null;
  const { data, error } = await supabase!.storage
    .from("documents")
    .createSignedUrl(path, 60 * 5);
  if (error) throw error;
  return data.signedUrl;
}
